/* @flow */

import * as React from 'react';
import { observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';

import Button from '@material-ui/core/Button';

import type TypeActionDrawerStore from 'stores/ActionDrawerStore.js';

import Loading from 'components/Loading/Loading';

import ActionDrawerHeader from '../../../components/ActionDrawerHeader/ActionDrawerHeader';
import AddNewCompanyForm from './AddNewCompanyForm';
import AddNewCompanySuccess from './AddNewCompanySuccess';
import Form from '../../../../../forms/_.extend';

import styles from '../../../ActionDrawer.module.scss';

type TypeProps = {|
  formData: Object,
  actionDrawerStore: TypeActionDrawerStore,
|} & TranslatorProps;

@translate()
@inject('actionDrawerStore')
@observer
export default class AddNewCompany extends React.Component<*> {
  @observable vModelData = this.props.model;

  /**
   * Default field mapping (overridable by parent via props)
   */
  defaultFieldList: Object = {
    account: 'chooseAccount.tradingAccount',
    company: 'location.locationCompany',
    site: 'location.locationSite',
    building: 'location.locationBuilding',
    room: 'location.locationRoom',
    system: 'location.locationSystem',
  };

  /**
   * Which fields to reset after success
   */
  resetFieldList: Array = ['site', 'building', 'room', 'system'];

  /**
   * Build form and submit hook.
   * This component supports BOTH modes: asset or rush.
   * Exclusivity rule: rush_* and asset_* are mutually exclusive.
   */
  createForm = () => {
    const { t }: TranslatorProps = this.props;
    const { actionDrawerStore }: TypeProps = this.props;
    const {
      parentForm,
      fieldList = null,

      isAssetRequest = false,
      locationType = null, // 'customer' | 'delivery' (asset)

      isRushAssetRequest = false,
      rushAssetRequestLocation = null, // 'customer' | 'delivery' (rush)
    }: Object = this.props;

    if (fieldList) {
      this.defaultFieldList = fieldList;
    }

    const fields: Array<Object> = [
      {
        name: 'addCompanyForm',
        label: t('general:actions.new_company'),
        title: 'New Company',
        fields: [
          {
            name: 'tradingAccount',
            label: t('general:trading_account'),
            rules: 'required|string',
            disabled: Boolean(parentForm.$(`${this.defaultFieldList.account}`).extra.selectID),
            value: parentForm.$(`${this.defaultFieldList.account}`).extra.selectID,
          },
          {
            name: 'company',
            label: t('general:company'),
            rules: 'required|string',
            value: '',
          },
        ],
      },
    ];

    const hooks: Object = {
      onSuccess: (data: Object) => {
        const { tradingAccount, company }: Object = data.values().addCompanyForm;

        const assetIntent = Boolean(
          isAssetRequest && (locationType === 'customer' || locationType === 'delivery')
        );

        const rushIntent = Boolean(
          isRushAssetRequest &&
            (rushAssetRequestLocation === 'customer' || rushAssetRequestLocation === 'delivery')
        );

        // Enforce XOR
        let sendAsset = false;
        let sendRush = false;
        let assetType = null;
        let rushType = null;

        if (assetIntent && !rushIntent) {
          sendAsset = true;
          assetType = locationType;
        } else if (rushIntent && !assetIntent) {
          sendRush = true;
          rushType = rushAssetRequestLocation;
        } else {
          console.warn(
            '[AddNewCompany] Ambiguous mode: assetIntent=%s rushIntent=%s. Sending none.',
            assetIntent,
            rushIntent
          );
        }

        actionDrawerStore.userAddCompany(
          {
            account_id: tradingAccount,
            name: company,
          },
          /* isAssetRequest */ sendAsset,
          /* asset location type */ sendAsset ? assetType : null,
          /* isRushAssetRequest */ sendRush,
          /* rush location type */ sendRush ? rushType : null
        );
      },
    };

    return new Form(
      { fields },
      {
        hooks,
        options: {
          validateOnInit: true,
          validateOnChange: true,
        },
        name: t('general:actions.new_company'),
      }
    );
  };

  /**
   * Reset child field values in the parent form after success
   */
  resetFieldObjects() {
    const { parentForm } = this.props;

    for (const fieldName in this.defaultFieldList) {
      if (this.resetFieldList[fieldName]) {
        const field: Object = parentForm.$(`${fieldName}`);
        const fieldExtra: Object = field.extra;

        field.set('value', '');
        field.set('extra', { ...fieldExtra, selectID: '' });
        field.set('extra', { ...fieldExtra, parentId: '' });
      }
    }
  }

  /**
   * Reset fetched data / external models (best-effort)
   */
  resetFieldData() {
    const { resetModels = [], resetFields = [] }: TypeProps = this.props;

    for (let i: * = 0; i < resetModels.length; i++) {
      if (resetModels[i]?.clear) resetModels[i].clear();
      if (resetModels[i]?.loadData) resetModels[i].loadData();
    }

    for (let i: * = 0; i < resetFields.length; i++) {
      if (resetFields[i]?.extra) resetFields[i].extra.selectID = '';
    }
  }

  /**
   * Render
   */
  render(): React.Element<*> {
    const { t }: TranslatorProps = this.props;
    const { actionDrawerStore }: TypeProps = this.props;
    const parentForm: Object = this.props.parentForm;

    if (actionDrawerStore.loading) {
      return <Loading />;
    }

    if (actionDrawerStore.error) {
      return (
        <div className={`${styles['action-drawer-form']} ${styles.error}`}>
          <ActionDrawerHeader title={t('general:actions.create_new_company')} />
          <div className={styles['action-drawer-content']}>
            {actionDrawerStore.errorResponse.message}
          </div>
          <div className={styles['action-drawer-actions']}>
            <Button
              color="primary"
              variant="contained"
              onClick={() => actionDrawerStore.closeActionDrawer()}
            >
              {t('general:actions.close')}
            </Button>
          </div>
        </div>
      );
    }

    if (actionDrawerStore.success) {
      return (
        <AddNewCompanySuccess
          parentForm={parentForm}
          model={this.props.model}
          fields={this.defaultFieldList}
          resetModels={this.props.resetModels}
          resetFields={this.props.resetFields}
          onMountHook={() => {
            this.resetFieldObjects();
            this.resetFieldData();
          }}
        />
      );
    }

    return (
      <div className={styles['action-drawer-form']}>
        <ActionDrawerHeader title={t('general:actions.create_new_company')} />
        <AddNewCompanyForm form={this.createForm()} accountsModel={this.props.accountsModel} />
      </div>
    );
  }
}
